import { defineStore } from 'pinia';
import { invoicePaymentLink } from '~/lib/invoicePaymentLink';
import { doc, getDoc, where } from 'firebase/firestore';
import type {
    Client,
    Invoice,
    InvoiceDeposit,
    InvoiceLineItem,
    InvoicePaymentPlan,
} from '~/types/models';
import { DEFAULT_CURRENCY } from '~/constants/currencies';
import { resolveDocumentRecipient } from '~/lib/documentRecipients';
import { dispatchNotification } from '~/lib/notificationDispatch';
import { buildDocumentCreatorFields } from '~/lib/documentCreator';
import { stripUndefined } from '~/lib/stripUndefined';
import { buildDocumentAccounting } from '~/lib/documentAccounting';
import { buildClientPortalDestination, buildPublicInvoiceLink, resolveClientEmailOrigin, resolveTenantOrigin } from '~/lib/tenantLinks';
import { generatePublicAccessToken } from '~/lib/publicAccessTokens';
import { toMillis } from '~/lib/timestamps';
import { runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import { resolveWorkspaceEntitlements } from '~/lib/subscriptionPlans';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';
import { assignCommercialDocumentNumber, reconcileMissingCommercialDocumentNumbers, requestNextCommercialDocumentNumber } from '~/lib/commercialDocumentNumbering';
import {
    createTenantDocument,
    fetchTenantCollection,
    updateTenantDocument,
    type TenantDocumentPayload,
} from '~/lib/tenantCollection';

type CreateInvoiceInput = {
    clientId?: string;
    projectId?: string;
    creator?: {
        createdBy?: string;
        createdByName?: string;
        createdByEmail?: string;
    };
    quoteId?: string;
    quoteSource?: 'full' | 'deposit' | 'balance' | 'installment';
    quoteInstallmentIndex?: number;
    invoiceNumber?: string;
    title?: string;
    issueDate?: string;
    dueDate?: string;
    currency?: string;
    footerMessage?: string;
    bankDetails?: string;
    allowThirdPartyPayments?: boolean;
    lineItems: InvoiceLineItem[];
    deposit?: {
        enabled: boolean;
        mode: 'fixed' | 'percentage';
        value: number;
    };
    paymentPlan?: InvoicePaymentPlan;
};

export const useInvoicesStore = defineStore('invoices', {
    state: () => ({
        invoices: [] as Invoice[],
        loading: false,
        saving: false,
        error: null as string | null,
        loadedForTenantId: null as string | null,
    }),

    actions: {
        _currentCreatorFields(override?: Pick<Invoice, 'createdBy' | 'createdByName' | 'createdByEmail'>) {
            if (override?.createdBy || override?.createdByName || override?.createdByEmail) {
                return buildDocumentCreatorFields({
                    userId: override.createdBy,
                    userName: override.createdByName,
                    userEmail: override.createdByEmail,
                });
            }

            const authStore = useAuthStore();
            const userStore = useUserStore();
            return buildDocumentCreatorFields({
                userId: authStore.user?.uid || userStore.user?.id,
                userName: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName,
                userEmail: authStore.user?.email || userStore.user?.email,
            });
        },

        _getTenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantStore.currentTenantId;
        },

        _sort(items: Invoice[]): Invoice[] {
            return [...items].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        },

        async _resolveClient(clientId?: string): Promise<Client | null> {
            const normalizedClientId = String(clientId || '').trim();
            if (!normalizedClientId) {
                return null;
            }

            const clientStore = useClientStore();
            const cached = clientStore.clients.find(client => client.id === normalizedClientId);
            if (cached?.email && cached?.name) {
                return cached;
            }

            return await clientStore.fetchClientById(normalizedClientId);
        },

        async _resolveDocumentRecipient(input: Pick<CreateInvoiceInput, 'clientId'>) {
            return await resolveDocumentRecipient({
                clientId: input.clientId,
            });
        },

        async _resolveDocumentLinkage(input: Pick<CreateInvoiceInput, 'clientId'>) {
            const resolved = await this._resolveDocumentRecipient(input);
            if (!resolved.clientId) {
                throw new Error('Link this invoice to a client before saving.');
            }
            return {
                clientId: resolved.clientId,
                resolved,
            };
        },

        _publicInvoiceOrigin(): string {
            const config = useRuntimeConfig();
            return resolveTenantOrigin({
                tenant: useTenantStore().currentTenant,
                appDomain: config.public.appDomain,
                fallbackOrigin: window.location.origin,
            });
        },

        buildInvoicePublicLink(invoice: Pick<Invoice, 'id' | 'publicAccessToken' | 'stripeCheckoutURL' | 'allowThirdPartyPayments'>): string | null {
            const token = String(invoice.publicAccessToken || '').trim();
            if (!token) {
                return null;
            }

            return buildPublicInvoiceLink({
                origin: this._publicInvoiceOrigin(),
                token,
                tenantId: this._getTenantId(),
                invoiceId: invoice.id,
            });
        },

        async ensureInvoicePublicLink(invoiceId: string, invoice?: Invoice): Promise<string> {
            const token = await this._ensurePublicAccessToken(invoiceId, invoice);
            return buildPublicInvoiceLink({
                origin: this._publicInvoiceOrigin(),
                token,
                tenantId: this._getTenantId(),
                invoiceId,
            });
        },

        async _ensurePublicAccessToken(invoiceId: string, invoice?: Invoice): Promise<string> {
            const existing = invoice || this.invoices.find(item => item.id === invoiceId);
            if (!existing) {
                throw new Error('Invoice not found.');
            }

            const currentToken = String(existing.publicAccessToken || '').trim();
            if (currentToken) {
                return currentToken;
            }

            const token = generatePublicAccessToken();
            await this.updateInvoice(invoiceId, {
                publicAccessToken: token,
                publicSharedAt: new Date(),
                updatedAt: new Date(),
            });

            return token;
        },

        _requireDocumentLinkage(input: Pick<CreateInvoiceInput, 'clientId'>) {
            const clientId = String(input.clientId || '').trim() || undefined;
            if (!clientId) {
                throw new Error('Link this invoice to a client before saving.');
            }

            return { clientId };
        },

        async _sendInvoiceEmail(invoice: Invoice, options?: { subject?: string; actionLink?: string; actionLabel?: string }): Promise<boolean> {
            const resolved = await this._resolveDocumentRecipient(invoice);
            if (!resolved.recipientEmail) {
                return false;
            }

            const actionLink = String(options?.actionLink || '').trim();
            if (!actionLink) {
                return false;
            }

            const tenantStore = useTenantStore();
            await $fetch('/api/send-email', {
                method: 'POST',
                headers: await getRequiredAuthHeaders(),
                body: {
                    tenantId: tenantStore.currentTenantId,
                    to: resolved.recipientEmail,
                    template: 'invoice_ready',
                    workspaceName: tenantStore.currentTenant?.name || 'Vuey',
                    clientName: resolved.recipientName,
                    recipientName: resolved.recipientName,
                    subject: options?.subject,
                    actionLink,
                    actionLabel: options?.actionLabel || 'View invoice',
                    documentTitle: invoice.title,
                    documentNumber: invoice.invoiceNumber,
                    amountTotal: Number(invoice.amountDue || invoice.amountTotal || 0),
                    currency: invoice.currency || DEFAULT_CURRENCY,
                    dueDate: invoice.dueDate,
                },
            });

            return true;
        },

        _buildInvoicePayload(input: CreateInvoiceInput, options?: {
            status?: Invoice['status'];
            stripeCheckoutURL?: string;
            stripeInvoiceId?: string;
            stripeCustomerId?: string;
            publicAccessToken?: string;
            publicSharedAt?: unknown;
            amountPaid?: number;
            paidAt?: unknown;
            paidBy?: string;
            creator?: Pick<Invoice, 'createdBy' | 'createdByName' | 'createdByEmail'>;
        }): Omit<Invoice, 'id'> {
            const tenantStore = useTenantStore();
            const entitlements = resolveWorkspaceEntitlements(tenantStore.currentTenant);
            if ((input.deposit?.enabled || input.paymentPlan?.enabled) && !entitlements.plan.features.depositsAndPaymentPlans) {
                throw new Error('Deposits and payment plans are available on the Growth plan and above.');
            }
            const {
                normalizedItems,
                amountTotal,
                deposit,
                paymentPlan,
            } = buildDocumentAccounting<InvoiceLineItem, InvoiceDeposit, InvoicePaymentPlan>({
                lineItems: input.lineItems,
                deposit: input.deposit,
                paymentPlan: input.paymentPlan,
            });
            const amountPaid = Number(options?.amountPaid || 0);
            const amountDue = Number(Math.max(0, amountTotal - amountPaid).toFixed(2));
            const footerMessage = String(input.footerMessage || tenantStore.currentTenant?.paymentSettings?.invoiceFooterMessage || '').trim() || undefined;
            const bankDetails = String(input.bankDetails || tenantStore.currentTenant?.paymentSettings?.bankDetails || '').trim() || undefined;
            const currency = String(input.currency || tenantStore.currentTenant?.paymentSettings?.defaultCurrency || DEFAULT_CURRENCY).trim() || DEFAULT_CURRENCY;

            return stripUndefined({
                tenantId: this._getTenantId(),
                createdBy: options?.creator?.createdBy,
                createdByName: options?.creator?.createdByName,
                createdByEmail: options?.creator?.createdByEmail,
                clientId: input.clientId || undefined,
                projectId: input.projectId || undefined,
                quoteId: input.quoteId || undefined,
                quoteSource: input.quoteSource || undefined,
                quoteInstallmentIndex: typeof input.quoteInstallmentIndex === 'number' ? input.quoteInstallmentIndex : undefined,
                invoiceNumber: input.invoiceNumber || undefined,
                title: input.title || undefined,
                issueDate: input.issueDate || undefined,
                dueDate: input.dueDate || undefined,
                currency,
                status: options?.status || 'draft',
                amountTotal,
                amountPaid,
                amountDue,
                lineItems: normalizedItems,
                deposit,
                paymentPlan,
                footerMessage,
                bankDetails,
                allowThirdPartyPayments: input.allowThirdPartyPayments !== false,
                stripeCheckoutURL: input.allowThirdPartyPayments === false ? '' : options?.stripeCheckoutURL,
                stripeInvoiceId: options?.stripeInvoiceId,
                stripeCustomerId: options?.stripeCustomerId,
                publicAccessToken: options?.publicAccessToken,
                publicSharedAt: options?.publicSharedAt,
                paidAt: options?.paidAt,
                paidBy: options?.paidBy,
                updatedAt: new Date(),
            }) as Omit<Invoice, 'id'>;
        },

        async fetchAll(options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const tenantStore = useTenantStore();
            this.error = null;

            try {
                await runFreshRequest(`invoices:${tenantId}:all`, async () => {
                    this.loading = true;
                    this.invoices = this._sort(await fetchTenantCollection<Invoice>($db, tenantId, 'invoices'));
                    if (this.invoices.some(invoice => !String(invoice.invoiceNumber || '').trim())
                        && ['owner', 'admin', 'member'].includes(String(tenantStore.currentTenantRole || ''))) {
                        try {
                            const assigned = await reconcileMissingCommercialDocumentNumbers('invoice');
                            const assignedById = new Map(assigned.map(item => [item.id, item.number]));
                            this.invoices = this._sort(this.invoices.map(invoice => ({
                                ...invoice,
                                ...(assignedById.has(invoice.id) ? { invoiceNumber: assignedById.get(invoice.id) } : {}),
                            })));
                        } catch (reconcileError) {
                            console.warn('Missing invoice numbers could not be reconciled:', reconcileError);
                        }
                    }
                    this.loadedForTenantId = tenantId;
                }, {
                    ...options,
                    hasData: this.loadedForTenantId === tenantId,
                });
            } catch (error) {
                this.error = `Error loading invoices: ${error}`;
                console.error(this.error);
            } finally {
                this.loading = false;
            }
        },

        async fetchById(invoiceId: string): Promise<Invoice | null> {
            const cached = this.invoices.find(item => item.id === invoiceId);
            if (cached) return cached;
            const { $db } = useNuxtApp();
            if (!$db) throw new Error('Firestore is not available.');
            const snapshot = await getDoc(doc($db, `tenants/${this._getTenantId()}/invoices`, invoiceId));
            if (!snapshot.exists()) return null;
            const invoice = { ...(snapshot.data() as Invoice), id: snapshot.id };
            this.invoices = this._sort([invoice, ...this.invoices]);
            return invoice;
        },

        async fetchForClient(clientId: string): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.loading = true;
            this.error = null;
            this.loadedForTenantId = null;

            try {
                this.invoices = this._sort(
                    await fetchTenantCollection<Invoice>($db, tenantId, 'invoices', [where('clientId', '==', clientId)]),
                );
            } catch (error) {
                this.error = `Error loading invoices: ${error}`;
                console.error(this.error);
                this.invoices = [];
            } finally {
                this.loading = false;
            }
        },

        async fetchForProject(projectId: string): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.loading = true;
            this.error = null;
            this.loadedForTenantId = null;

            try {
                this.invoices = this._sort(
                    await fetchTenantCollection<Invoice>($db, tenantId, 'invoices', [where('projectId', '==', projectId)]),
                );
            } catch (error) {
                this.error = `Error loading project invoices: ${error}`;
                console.error(this.error);
                this.invoices = [];
            } finally {
                this.loading = false;
            }
        },

        async createInvoice(input: CreateInvoiceInput): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.saving = true;
            this.error = null;
            let createdInvoiceId = '';

            try {
                const linkage = this._requireDocumentLinkage(input);
                const resolvedLinkage = await this._resolveDocumentLinkage(linkage);
                const invoiceNumber = String(input.invoiceNumber || '').trim()
                    || await requestNextCommercialDocumentNumber('invoice', resolvedLinkage.clientId);
                const payload = {
                    ...this._buildInvoicePayload({
                        ...input,
                        invoiceNumber: undefined,
                        clientId: resolvedLinkage.clientId,
                    }, {
                        creator: this._currentCreatorFields(input.creator),
                    }),
                    tenantId,
                    createdAt: new Date(),
                };

                const invoiceId = await createTenantDocument($db, tenantId, 'invoices', payload as TenantDocumentPayload);
                createdInvoiceId = invoiceId;
                await assignCommercialDocumentNumber('invoice', invoiceId, invoiceNumber);
                this.invoices = this._sort([
                    { ...(payload as Invoice), id: invoiceId, invoiceNumber },
                    ...this.invoices,
                ]);

                return invoiceId;
            } catch (error: any) {
                if (createdInvoiceId) {
                    await $fetch('/api/commercial-documents/delete', {
                        method: 'POST', headers: await getRequiredAuthHeaders(),
                        body: { tenantId, kind: 'invoice', id: createdInvoiceId },
                    }).catch(() => undefined);
                }
                this.error = error?.message || 'Failed to create invoice.';
                console.error('Error creating invoice:', error);
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async saveInvoice(invoiceId: string, input: CreateInvoiceInput): Promise<void> {
            const existing = this.invoices.find(item => item.id === invoiceId);
            if (!existing) {
                throw new Error('Invoice not found.');
            }
            const linkage = this._requireDocumentLinkage({
                clientId: input.clientId ?? existing.clientId,
            });
            const resolvedLinkage = await this._resolveDocumentLinkage(linkage);

            const payload = this._buildInvoicePayload({
                ...input,
                clientId: resolvedLinkage.clientId,
            }, {
                status: Number(existing.amountPaid || 0) > 0 ? existing.status : 'draft',
                amountPaid: Number(existing.amountPaid || 0),
                paidAt: existing.status === 'paid' ? existing.paidAt : undefined,
                paidBy: existing.status === 'paid' ? existing.paidBy : undefined,
                creator: existing.status === 'draft'
                    ? this._currentCreatorFields(input.creator || {
                        createdBy: existing.createdBy,
                        createdByName: existing.createdByName,
                        createdByEmail: existing.createdByEmail,
                    })
                    : {
                        createdBy: existing.createdBy,
                        createdByName: existing.createdByName,
                        createdByEmail: existing.createdByEmail,
                    },
            });

            await this.updateInvoice(invoiceId, payload);
        },

        async sendInvoice(invoiceId: string): Promise<void> {
            const existing = this.invoices.find(item => item.id === invoiceId);
            const tenantStore = useTenantStore();
            if (!existing) {
                throw new Error('Invoice not found.');
            }
            const resolved = await this._resolveDocumentRecipient(existing);
            if (!resolved.recipientEmail) {
                throw new Error('This client needs an email address before the invoice can be sent.');
            }

            const stripeEnabled = existing.allowThirdPartyPayments !== false
                && Boolean(tenantStore.currentTenant?.paymentSettings?.stripeAccountId);
            const config = useRuntimeConfig();
            const portalOrigin = resolveClientEmailOrigin({
                tenant: tenantStore.currentTenant,
                appDomain: config.public.appDomain,
                fallbackOrigin: window.location.origin,
            });
            const clientSlug = String(resolved.client?.slug || '').trim();
            const portalDestination = clientSlug
                ? buildClientPortalDestination({
                    origin: portalOrigin,
                    clientSlug,
                    tenantId: this._getTenantId(),
                    section: 'invoices',
                    focusParam: 'focusInvoiceId',
                    focusValue: invoiceId,
                })
                : null;
            let publicAccessToken = String(existing.publicAccessToken || '').trim();

            publicAccessToken = await this._ensurePublicAccessToken(invoiceId, existing);
            const publicInvoiceLink = buildPublicInvoiceLink({
                origin: this._publicInvoiceOrigin(),
                token: publicAccessToken,
                tenantId: this._getTenantId(),
                invoiceId,
            });

            await this.updateInvoice(invoiceId, {
                clientId: existing.clientId || resolved.clientId || undefined,
                status: existing.status === 'draft' ? 'sent' : existing.status,
                stripeCheckoutURL: stripeEnabled ? invoicePaymentLink({ ...existing, status: existing.status === 'draft' ? 'sent' : existing.status, publicAccessToken }, this._getTenantId()) : '',
                publicAccessToken,
                publicSharedAt: new Date(),
                updatedAt: new Date(),
            });

            const sentInvoice = this.invoices.find(item => item.id === invoiceId) || {
                ...existing,
                clientId: existing.clientId || resolved.clientId || undefined,
                status: existing.status === 'draft' ? 'sent' : existing.status,
                stripeCheckoutURL: stripeEnabled ? invoicePaymentLink({ ...existing, status: existing.status === 'draft' ? 'sent' : existing.status, publicAccessToken }, this._getTenantId()) : '',
                publicAccessToken,
            };

            try {
                await this._sendInvoiceEmail(sentInvoice as Invoice, {
                    actionLink: publicInvoiceLink,
                    actionLabel: 'View invoice',
                });
            } catch (error) {
                console.warn('Invoice email could not be sent:', error);
            }

            try {
                if (resolved.clientId && clientSlug) {
                    await dispatchNotification({
                        tenantId: this._getTenantId(),
                        surface: 'client',
                        eventType: 'invoice_issued',
                        title: existing.title ? `Invoice issued: ${existing.title}` : 'A new invoice is ready',
                        body: existing.dueDate
                            ? `Due ${existing.dueDate}.`
                            : 'A new invoice is ready in the portal.',
                        recipients: [{
                            type: 'portal_access',
                            clientId: resolved.clientId,
                            projectId: existing.projectId,
                            permission: 'viewInvoices',
                        }],
                        entityType: 'invoice',
                        entityId: invoiceId,
                        clientId: resolved.clientId,
                        projectId: existing.projectId,
                        actionPath: portalDestination?.actionPath,
                        actionLabel: 'Open invoice',
                        email: {
                            template: 'invoice_ready',
                            actionLink: publicInvoiceLink,
                            actionLabel: 'View invoice',
                            clientName: resolved.recipientName,
                            documentTitle: existing.title,
                            documentNumber: existing.invoiceNumber,
                            amountTotal: Number(existing.amountDue || existing.amountTotal || 0),
                            currency: existing.currency || DEFAULT_CURRENCY,
                            dueDate: existing.dueDate,
                        },
                    });
                }
            } catch (notificationError) {
                console.warn('Invoice notification could not be created:', notificationError);
            }
        },

        async sendInvoiceReminder(invoiceId: string): Promise<void> {
            const existing = this.invoices.find(item => item.id === invoiceId);
            if (!existing) {
                throw new Error('Invoice not found.');
            }
            if (existing.status === 'draft' || existing.status === 'paid') {
                throw new Error('Only sent invoices that still need payment can be reminded.');
            }
            const resolved = await this._resolveDocumentRecipient(existing);
            const tenantStore = useTenantStore();
            const invoiceLabel = existing.title || existing.invoiceNumber || 'your invoice';
            const config = useRuntimeConfig();
            const clientSlug = String(resolved.client?.slug || '').trim();
            const portalDestination = clientSlug
                ? buildClientPortalDestination({
                    origin: resolveClientEmailOrigin({
                        tenant: tenantStore.currentTenant,
                        appDomain: config.public.appDomain,
                        fallbackOrigin: window.location.origin,
                    }),
                    clientSlug,
                    tenantId: this._getTenantId(),
                    section: 'invoices',
                    focusParam: 'focusInvoiceId',
                    focusValue: invoiceId,
                })
                : null;
            const publicAccessToken = await this._ensurePublicAccessToken(invoiceId, existing);
            await this.updateInvoice(invoiceId, {
                publicAccessToken,
                publicSharedAt: new Date(),
                updatedAt: new Date(),
            });
            const publicInvoiceLink = buildPublicInvoiceLink({
                origin: this._publicInvoiceOrigin(),
                token: publicAccessToken,
                tenantId: this._getTenantId(),
                invoiceId,
            });
            const emailSent = await this._sendInvoiceEmail(existing, {
                subject: `Reminder: ${tenantStore.currentTenant?.name || 'Vuey'} sent ${invoiceLabel}`,
                actionLink: publicInvoiceLink,
                actionLabel: 'View invoice',
            });

            if (!emailSent) {
                throw new Error('The linked client needs an email address before a reminder can be sent.');
            }

            try {
                const authStore = useAuthStore();
                const userStore = useUserStore();
                if (resolved.clientId && clientSlug) {
                    await dispatchNotification({
                        tenantId: this._getTenantId(),
                        surface: 'client',
                        eventType: 'invoice_due',
                        title: `Reminder: ${invoiceLabel} still needs attention`,
                        body: existing.dueDate
                            ? `This invoice is still open and due ${existing.dueDate}.`
                            : 'This invoice is still open and waiting for payment.',
                        recipients: [{
                            type: 'portal_access',
                            clientId: resolved.clientId,
                            projectId: existing.projectId,
                            permission: 'viewInvoices',
                        }],
                        entityType: 'invoice',
                        entityId: invoiceId,
                        clientId: resolved.clientId,
                        projectId: existing.projectId,
                        actionPath: portalDestination?.actionPath,
                        actionLabel: 'Open invoice',
                        actorId: authStore.user?.uid || userStore.user?.id,
                        actorName: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName,
                        actorAvatarURL: userStore.user?.avatarURL,
                    });
                }
            } catch (notificationError) {
                console.warn('Invoice reminder notification could not be created:', notificationError);
            }
        },

        async updateInvoice(invoiceId: string, updates: Partial<Invoice>): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.error = null;

            try {
                const requestedNumber = Object.prototype.hasOwnProperty.call(updates, 'invoiceNumber')
                    ? String(updates.invoiceNumber || '').trim()
                    : '';
                const { invoiceNumber: _ignoredInvoiceNumber, ...safeUpdates } = updates;
                const assignedNumber = requestedNumber
                    ? await assignCommercialDocumentNumber('invoice', invoiceId, requestedNumber)
                    : undefined;
                const patch = stripUndefined({
                    ...safeUpdates,
                    updatedAt: new Date(),
                });
                await updateTenantDocument($db, tenantId, 'invoices', invoiceId, patch as TenantDocumentPayload);

                const idx = this.invoices.findIndex(item => item.id === invoiceId);
                if (idx >= 0) {
                    const existing = this.invoices[idx] as Invoice;
                    this.invoices[idx] = {
                        ...existing,
                        ...patch,
                        ...(assignedNumber ? { invoiceNumber: assignedNumber } : {}),
                    } as Invoice;
                    this.invoices = this._sort([...this.invoices]);
                }
            } catch (error: any) {
                this.error = error?.message || 'Failed to update invoice.';
                console.error('Error updating invoice:', error);
                throw error;
            }
        },

        async markInvoicePaid(invoiceId: string): Promise<void> {
            const target = this.invoices.find(item => item.id === invoiceId);
            if (!target) return;

            await this.updateInvoice(invoiceId, {
                status: 'paid',
                amountPaid: Number(target.amountTotal || 0),
                amountDue: 0,
                paidAt: new Date(),
            });

            try {
                const { $auth } = useNuxtApp();
                const authUser = $auth.currentUser;
                if (authUser && target.clientId) {
                    const token = await authUser.getIdToken();
                    await $fetch('/api/onboarding/events', {
                        method: 'POST',
                        body: {
                            tenantId: this._getTenantId(),
                            clientId: target.clientId,
                            projectId: target.projectId,
                            resourceId: invoiceId,
                            event: 'invoice_paid',
                        },
                        headers: {
                            authorization: `Bearer ${token}`,
                        },
                    });
                }
            } catch (automationError) {
                console.warn('Invoice paid onboarding automation could not be applied:', automationError);
            }

            try {
                const { tenantPath } = useTenantPath();
                await dispatchNotification({
                    tenantId: this._getTenantId(),
                    surface: 'tenant',
                    eventType: 'invoice_paid',
                    title: target.title ? `Invoice paid: ${target.title}` : 'Invoice paid',
                    body: 'A client payment has been recorded.',
                    recipients: [{ type: 'tenant_roles', roles: ['owner', 'admin'] }],
                    entityType: 'invoice',
                    entityId: invoiceId,
                    clientId: target.clientId,
                    projectId: target.projectId,
                    actionPath: tenantPath({ path: '/invoices', query: { focusInvoiceId: invoiceId } }),
                    actionLabel: 'Open invoice',
                });
            } catch (notificationError) {
                console.warn('Invoice paid notification could not be created:', notificationError);
            }
        },

        async deleteInvoice(invoiceId: string): Promise<void> {
            const { $auth } = useNuxtApp();
            const tenantId = this._getTenantId();
            const existing = this.invoices.find(item => item.id === invoiceId);
            if (!existing) {
                throw new Error('Invoice not found.');
            }

            this.error = null;

            try {
                if (!$auth.currentUser) throw new Error('Please sign in before deleting an invoice.');
                await $fetch('/api/commercial-documents/delete', {
                    method: 'POST',
                    headers: { authorization: `Bearer ${await $auth.currentUser.getIdToken()}` },
                    body: { tenantId, kind: 'invoice', id: invoiceId },
                });
                this.invoices = this.invoices.filter(item => item.id !== invoiceId);

                if (existing.quoteId) {
                    const quotesStore = useQuotesStore();
                    const quote = quotesStore.quotes.find(item => item.id === existing.quoteId);

                    if (quote) {
                        const nextGeneratedInvoiceIds = (quote.generatedInvoiceIds || []).filter(id => id !== invoiceId);
                        const nextInstallments = quote.paymentPlan?.installments?.map((installment, index) => {
                            if (existing.quoteSource === 'installment' && index === existing.quoteInstallmentIndex) {
                                return {
                                    ...installment,
                                    status: 'pending' as const,
                                };
                            }
                            return installment;
                        });

                        Object.assign(quote, {
                            generatedInvoiceIds: nextGeneratedInvoiceIds,
                            paymentPlan: quote.paymentPlan
                                ? {
                                    ...quote.paymentPlan,
                                    installments: nextInstallments || quote.paymentPlan.installments,
                                }
                                : undefined,
                        });
                    }
                }
            } catch (error: any) {
                this.error = error?.message || 'Failed to delete invoice.';
                console.error('Error deleting invoice:', error);
                throw error;
            }
        },
    },

    getters: {
        invoicesByClient: (state) => (clientId: string) => {
            const normalizedClientId = String(clientId || '').trim();
            if (!normalizedClientId) {
                return [];
            }

            return state.invoices.filter((item) => item.clientId === normalizedClientId);
        },
        invoicesByProject: (state) => (projectId: string) => state.invoices.filter(item => item.projectId === projectId),
        outstandingTotal: (state) =>
            state.invoices.reduce((sum, invoice) => {
                if (invoice.status === 'paid') return sum;
                return sum + Number(invoice.amountDue || 0);
            }, 0),
    },
});
